/**
 * Support-report vocabularies — the fixed lists the "Report a problem" flow
 * offers instead of a free-text box.
 *
 * WHY THESE ARE HAND-AUTHORED. The design doc (Phase 1 Support Intake) says the
 * intent list is "generated from what this screen can actually do". It is not,
 * and cannot be yet: the screen→operation map covers six surfaces and the route
 * list is roughly a hundred. Generation is the Phase 2 story. Until then these
 * are curated per route FAMILY, with a generic set everywhere else, and
 * "None of these" is the monitoring signal that a family's list is wrong or
 * missing (operator ruling, 2026-09-21, Nick).
 *
 * EVERY entry is a literal we wrote ourselves. That is the whole PII argument:
 * an enum cannot carry a customer's name the way a text box can. Nothing in
 * this file may ever be interpolated from record data.
 *
 * i18n: each row carries its full catalog key plus the byte-exact English as
 * the fallback sibling, resolved `t(row.key, row.en)` at RENDER time — the
 * static-config-table convention in CLAUDE.md. None of these keys exist in the
 * kit catalog yet; they travel as one CLAUDE-CODE-REQUESTS entry (REQ-SR-01).
 */

export interface VocabRow {
  /** Stable value sent in the report. Never localized, never rendered. */
  id: string;
  /** Full catalog key. */
  key: string;
  /** Byte-exact English, used as the t() fallback until the key ships. */
  en: string;
}

export interface RouteVocabulary {
  /** Family id, sent in the report so a missing vocabulary is attributable. */
  family: string;
  /** Task-phrased (operator choice, 2026-09-21): "Changing this customer's type". */
  intents: VocabRow[];
  /** Area chips — noun-phrased, the part of the page rather than the task. */
  areas: VocabRow[];
}

const v = (family: string, kind: string, id: string, en: string): VocabRow => ({
  id,
  key: `support.vocab.${family}.${kind}.${id}`,
  en,
});

/** Families in match order. The FIRST prefix that matches a route id wins, so
 *  longer prefixes are listed before their parents. */
const FAMILIES: Array<{ family: string; prefixes: string[]; vocab: RouteVocabulary }> = [
  {
    family: "customers",
    prefixes: ["customers", "settings.customerTypes"],
    vocab: {
      family: "customers",
      intents: [
        v("customers", "intent", "changeType", "Changing this customer's type"),
        v("customers", "intent", "editDetails", "Editing this customer's details"),
        v("customers", "intent", "addNote", "Adding or editing a note"),
        v("customers", "intent", "viewHistory", "Looking at their appointments or history"),
        v("customers", "intent", "findCustomer", "Finding a customer"),
      ],
      areas: [
        v("customers", "area", "customerType", "Customer type"),
        v("customers", "area", "customerDetails", "Customer details"),
        v("customers", "area", "notes", "Notes"),
        v("customers", "area", "appointments", "Appointments"),
        v("customers", "area", "documents", "Documents"),
        v("customers", "area", "export", "Export"),
      ],
    },
  },
  {
    family: "appointments",
    prefixes: ["appointments", "appointment.", "calendar.", "floor"],
    vocab: {
      family: "appointments",
      intents: [
        v("appointments", "intent", "book", "Booking an appointment"),
        v("appointments", "intent", "reschedule", "Rescheduling or cancelling one"),
        v("appointments", "intent", "checkIn", "Checking someone in"),
        v("appointments", "intent", "viewCalendar", "Looking at the calendar"),
        v("appointments", "intent", "changeAvailability", "Changing availability"),
      ],
      areas: [
        v("appointments", "area", "booking", "Booking"),
        v("appointments", "area", "reschedule", "Reschedule or cancel"),
        v("appointments", "area", "checkIn", "Check-in"),
        v("appointments", "area", "calendarView", "Calendar view"),
        v("appointments", "area", "availability", "Availability"),
        v("appointments", "area", "notifications", "Confirmations and reminders"),
      ],
    },
  },
  {
    family: "events",
    prefixes: ["events", "eventday", "event.", "templates", "ticket-", "guests", "registrations", "checkins", "newjourneys", "journeys"],
    vocab: {
      family: "events",
      intents: [
        v("events", "intent", "createEvent", "Creating an event"),
        v("events", "intent", "editEvent", "Editing an event"),
        v("events", "intent", "publishEvent", "Publishing an event"),
        v("events", "intent", "manageGuests", "Managing the guest list"),
        v("events", "intent", "runEventDay", "Running the event on the day"),
      ],
      areas: [
        v("events", "area", "eventDetails", "Event details"),
        v("events", "area", "schedule", "Schedule"),
        v("events", "area", "tickets", "Tickets"),
        v("events", "area", "guestList", "Guest list"),
        v("events", "area", "landingPage", "Landing page"),
        v("events", "area", "messaging", "Messaging"),
      ],
    },
  },
  {
    family: "queues",
    prefixes: ["queues", "queue.", "ops.walkins", "ops.today"],
    vocab: {
      family: "queues",
      intents: [
        v("queues", "intent", "serveNext", "Serving the next person"),
        v("queues", "intent", "addWalkIn", "Adding a walk-in"),
        v("queues", "intent", "viewBoard", "Looking at the queue board"),
        v("queues", "intent", "changeQueueSetup", "Changing how a queue is set up"),
      ],
      areas: [
        v("queues", "area", "board", "Queue board"),
        v("queues", "area", "walkIn", "Walk-in"),
        v("queues", "area", "serving", "Serving"),
        v("queues", "area", "display", "Lobby display"),
        v("queues", "area", "queueSettings", "Queue settings"),
      ],
    },
  },
  {
    family: "reports",
    prefixes: ["reports"],
    vocab: {
      family: "reports",
      intents: [
        v("reports", "intent", "runReport", "Running a report"),
        v("reports", "intent", "exportResults", "Exporting the results"),
        v("reports", "intent", "changeRange", "Changing the date range or filters"),
        v("reports", "intent", "readDashboard", "Reading a dashboard"),
      ],
      areas: [
        v("reports", "area", "filters", "Filters"),
        v("reports", "area", "dateRange", "Date range"),
        v("reports", "area", "chart", "Chart"),
        v("reports", "area", "table", "Table"),
        v("reports", "area", "export", "Export"),
      ],
    },
  },
  {
    family: "settings",
    prefixes: ["settings", "locations", "staff", "resources", "nodes", "branding", "approvals", "orders", "account.", "workforce.setup"],
    vocab: {
      family: "settings",
      intents: [
        v("settings", "intent", "changeSetting", "Changing a setting"),
        v("settings", "intent", "editLocation", "Adding or editing a location"),
        v("settings", "intent", "manageStaff", "Managing staff or teams"),
        v("settings", "intent", "setUpIntegration", "Setting up an integration"),
        v("settings", "intent", "changeBranding", "Changing branding"),
      ],
      areas: [
        v("settings", "area", "locations", "Locations"),
        v("settings", "area", "staff", "Staff and teams"),
        v("settings", "area", "integrations", "Integrations"),
        v("settings", "area", "branding", "Branding"),
        v("settings", "area", "security", "Security and access"),
        v("settings", "area", "billing", "Billing"),
      ],
    },
  },
];

/** Everywhere the six families do not reach. Deliberately vague — a vague
 *  generic answer plus a high "none of these" rate is what tells us a real
 *  vocabulary is owed for that route. */
export const GENERIC_VOCABULARY: RouteVocabulary = {
  family: "generic",
  intents: [
    v("generic", "intent", "saveSomething", "Saving or submitting something"),
    v("generic", "intent", "findSomething", "Finding something"),
    v("generic", "intent", "somethingWrong", "Something on the page looked wrong"),
  ],
  areas: [
    v("generic", "area", "mainContent", "The main content"),
    v("generic", "area", "form", "A form"),
    v("generic", "area", "listTable", "A list or table"),
    v("generic", "area", "controls", "A button or menu"),
    v("generic", "area", "navigation", "Navigation"),
  ],
};

/** Always appended to the intent list. Picking it opens the detail ladder
 *  automatically — on its own the answer tells us nothing. */
export const SOMETHING_ELSE: VocabRow = v("generic", "intent", "somethingElse", "Something else on this page");

/** Always appended to the area chips. Its RATE per route is the signal that a
 *  vocabulary is wrong (design doc §6, "the vocabularies drift"). */
export const NONE_OF_THESE: VocabRow = v("generic", "area", "noneOfThese", "None of these");

export function vocabularyForRoute(route: string): RouteVocabulary {
  for (const f of FAMILIES) {
    if (f.prefixes.some((p) => route === p || route.startsWith(p.endsWith(".") ? p : p + "."))) return f.vocab;
  }
  return GENERIC_VOCABULARY;
}

/** Six fixed strings. The ONLY diagnostic signal that exists when nothing threw
 *  — "it saved the wrong thing" has no error code behind it. */
export const OUTCOMES: VocabRow[] = [
  v("outcome", "opt", "sawError", "I saw an error message"),
  v("outcome", "opt", "nothingHappened", "Nothing happened"),
  v("outcome", "opt", "wrongInfo", "Wrong information shown"),
  v("outcome", "opt", "savedWrong", "It saved the wrong thing"),
  v("outcome", "opt", "tooSlow", "Too slow"),
  v("outcome", "opt", "cantFind", "Couldn't find the control"),
];

export const FREQUENCIES: VocabRow[] = [
  v("frequency", "opt", "everyTime", "Every time"),
  v("frequency", "opt", "sometimes", "Sometimes"),
  v("frequency", "opt", "justOnce", "Just once"),
];

export const LAST_WORKED: VocabRow[] = [
  v("lastWorked", "opt", "firstTime", "First time"),
  v("lastWorked", "opt", "earlierToday", "Earlier today"),
  v("lastWorked", "opt", "lastWeek", "Last week"),
];
